import * as yup from "yup";
import { parseWorkItemEstimateInputToMinutes } from "../utils/workItemEstimateInput";
import { parseJiraLogDurationToMinutes } from "../utils/jiraLogDuration";
import {
  containsDisallowedMarkup,
  PLAIN_TEXT_MARKUP_MESSAGE,
  sanitizePlainTextInput,
} from "../utils/plainTextInput";

export { PLAIN_TEXT_MARKUP_MESSAGE, sanitizePlainTextInput };

/** Yup string field that rejects HTML/script injection (XSS hardening). */
export function plainTextYup(options = {}) {
  const { required = false, requiredMessage, min, max, label = "Field" } = options;
  let schema = yup.string();
  if (required) {
    schema = schema.required(requiredMessage ?? `${label} is required`);
  }
  schema = schema.test("plain-text-safe", PLAIN_TEXT_MARKUP_MESSAGE, (value) => {
    if (value == null || String(value).trim() === "") return true;
    return !containsDisallowedMarkup(String(value));
  });
  if (min != null) {
    schema = schema.min(min, `${label} must be at least ${min} characters`);
  }
  if (max != null) {
    schema = schema.max(max, `${label} must be at most ${max} characters`);
  }
  return schema.trim();
}

/** Add/Edit User: billing rate is optional; when set, must be a finite number ≥ 0. */
const optionalBillingRatePerHourField = yup
  .mixed()
  .test("billing-rate-type", "Billing rate must be a number", (value) => {
    if (value === "" || value == null) return true;
    const n = typeof value === "number" ? value : Number(String(value).trim());
    return Number.isFinite(n);
  })
  .test("billing-rate-min", "Billing rate must be 0 or greater", (value) => {
    if (value === "" || value == null) return true;
    const n = typeof value === "number" ? value : Number(String(value).trim());
    return Number.isFinite(n) && n >= 0;
  });

/** True if value is a non-empty YYYY-MM-DD string that matches a real calendar day (local). */
function isValidYmdLocal(value) {
  if (value == null) return false;
  const s = String(value).trim();
  if (s === "") return false;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return false;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const dt = new Date(y, mo - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === mo - 1 && dt.getDate() === d;
}

/** One SaaS module row on Add/Edit Client — dates may be left blank. */
function saasModuleFormRowSchema() {
  const optionalYmd = yup
    .mixed()
    .transform((v) => (v == null ? "" : String(v).trim()))
    .test("ymd-or-empty", "Enter a valid date or leave blank", (v) => !v || isValidYmdLocal(v));

  return yup.object({
    implementationFee: yup.mixed().nullable(),
    monthlyFee: yup.mixed().nullable(),
    additionalFee: yup.string().nullable(),
    billingType: yup.string().nullable(),
    startDate: optionalYmd,
    endDate: optionalYmd.test(
      "end-after-start",
      "End date must be on or after start date",
      function (ev) {
        const v = ev == null ? "" : String(ev).trim();
        const s = this.parent.startDate;
        const sv = s == null ? "" : String(s).trim();
        if (!v || !sv) return true;
        if (!isValidYmdLocal(v) || !isValidYmdLocal(sv)) return true;
        return v >= sv;
      }
    ),
    active: yup.boolean().nullable(),
  });
}

function addClientSaasModulesSchema(value) {
  if (value == null) return yup.mixed().nullable();
  if (typeof value !== "object" || Array.isArray(value)) return yup.object().nullable();
  const keys = Object.keys(value);
  if (keys.length === 0) return yup.object().nullable();
  const shape = {};
  const row = saasModuleFormRowSchema();
  keys.forEach((k) => {
    shape[k] = row;
  });
  return yup.object().shape(shape);
}

export const LoginSchema = yup.object().shape({
  Email: yup.string().email("Please enter a valid email address").required("Email is required"),
  Password: yup
    .string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
});

export const ForgotPasswordSchema = yup.object().shape({
  Email: yup.string().email("Please enter a valid email address").required("Email is required"),
});

export const AddClientSchema = yup.object().shape({
  clientName: plainTextYup({
    required: true,
    requiredMessage: "Client Name is required",
    label: "Client name",
  }),
  qbClientName: plainTextYup({
    required: true,
    requiredMessage: "QB Client Name is required",
    label: "QB client name",
  }),
  contactPerson: plainTextYup({
    required: true,
    requiredMessage: "Contact Person is required",
    label: "Contact person",
  }),
  status: yup.string().required("Status is required"),
  contactEmail: yup.string().email("Please enter a valid email address").nullable(),
  maxHoursMonthly: yup
    .mixed()
    .transform((value, originalValue) => (originalValue === "" ? undefined : Number(value)))
    .test(
      "is-number",
      "Max Hours Monthly must be a number",
      (value) => value === undefined || !isNaN(value)
    )
    .nullable(),
  // hoursLimitPeriod: yup.string().required("Hours Limit Period is required"),
  // hoursLimit: yup
  //   .mixed()
  //   .transform((value, originalValue) => {
  //     return originalValue === "" ? undefined : Number(value);
  //   })
  //   .test("is-number", "Hours Limit must be a number", (value) => {
  //     return value !== undefined && !isNaN(value);
  //   })
  //   .test("is-positive", "Hours Limit must be greater than 0", (value) => {
  //     return value !== undefined && value > 0;
  //   })
  //   .required("Hours Limit is required"),
  notes: plainTextYup({ label: "Notes", max: 2000 }).nullable(),
  isSaasClient: yup.boolean().default(false),
  saasPlanName: plainTextYup({ label: "SaaS plan name", max: 200 }).nullable(),
  saasBillingCycle: yup.string().trim().nullable(),
  domainRegistrar: plainTextYup({ label: "Domain registrar", max: 255 }).nullable(),
  saasModules: yup.lazy((value) => addClientSaasModulesSchema(value)),
});

/** Schema for create user (invite) – email + name only; system sends verification email */
export const AddUserInviteSchema = yup.object().shape({
  email: yup.string().email("Invalid email format").required("Email is required"),
  name: yup.string().trim().required("Name is required"),
});

/** Add User – matches backend payload: email, roleId (static), firstName, lastName, name, teamId, jobTitle, billingRatePerHour (optional), is_active, sendInviteEmail, forcePasswordResetOnFirstLogin, mobileNumber, whatsappNumber, receiveWeeklyActivitySummary. UI includes role dropdown (roleId sent to API is static for now). */
export const AddUserSchema = yup.object().shape({
  role: yup.string().required("Role is required"),
  firstName: yup.string().trim().required("First Name is required"),
  lastName: yup.string().trim().required("Last Name is required"),
  email: yup.string().email("Invalid email format").required("Email is required"),
  username: yup.string().nullable(),
  jobTitle: yup.string().trim().required("Job Title is required"),
  teamId: yup.string().trim().required("Team is required"),
  billingRatePerHour: optionalBillingRatePerHourField,
  status: yup.string().required("Status is required"),
  mobileNumber: yup.string().nullable(),
  whatsappNumber: yup.string().nullable(),
  sendInviteEmail: yup.boolean(),
  forcePasswordResetOnFirstLogin: yup.boolean(),
  receiveWeeklyActivitySummary: yup.boolean(),
});

/** Edit User – PUT payload: firstName, lastName, roleId, teamId, jobTitle, billingRatePerHour (optional), is_active, mobileNumber, whatsappNumber, receiveWeeklyActivitySummary. Email/username are display-only. */
export const EditUserSchema = yup.object().shape({
  firstName: yup.string().trim().required("First Name is required"),
  lastName: yup.string().trim().required("Last Name is required"),
  email: yup.string().email("Invalid email format").nullable(),
  username: yup.string().nullable(),
  roleId: yup.string().trim().required("Role is required"),
  teamId: yup.string().trim().required("Team is required"),
  jobTitle: yup.string().trim().required("Job Title is required"),
  billingRatePerHour: optionalBillingRatePerHourField,
  status: yup.string().required("Status is required"),
  mobileNumber: yup.string().nullable(),
  whatsappNumber: yup.string().nullable(),
  receiveWeeklyActivitySummary: yup.boolean(),
});

export const AddTeamSchema = yup.object().shape({
  teamName: plainTextYup({
    required: true,
    requiredMessage: "Team Name is required",
    label: "Team name",
  }),
  status: yup.string().required("Status is required"),
});

export const AddRoleSchema = yup.object().shape({
  roleName: plainTextYup({
    required: true,
    requiredMessage: "Role name is required",
    label: "Role name",
    min: 2,
    max: 50,
  }),
  description: plainTextYup({ label: "Description", max: 200 }).nullable(),
  status: yup.string().required("Status is required"),
});

export const EditRoleSchema = AddRoleSchema;

/** Settings → System lists (client tag, vendor tag, job title, etc.). */
export const SystemListItemNameSchema = yup.object().shape({
  name: plainTextYup({
    required: true,
    requiredMessage: "Name is required",
    label: "Name",
    max: 100,
  }),
  isActive: yup.boolean().default(true),
});

/** Settings → System lists → SaaS modules master list. */
export const SaasModuleMasterSchema = yup.object().shape({
  name: plainTextYup({
    required: true,
    requiredMessage: "Module name is required",
    label: "Module name",
    max: 100,
  }),
  isActive: yup.boolean().default(true),
});

/**
 * Validate modal/form data with a Yup schema (non–react-hook-form flows).
 * @param {import("yup").ObjectSchema} schema
 * @param {object} data
 * @returns {Promise<{ valid: true, values: object } | { valid: false, message: string }>}
 */
export async function validateFormWithSchema(schema, data) {
  try {
    const values = await schema.validate(data, { abortEarly: true, stripUnknown: true });
    return { valid: true, values };
  } catch (err) {
    return { valid: false, message: err?.message ?? "Validation failed" };
  }
}

export const AddTeamMemberSchema = yup.object().shape({
  email: yup.string().email("Please enter a valid email address").required("User is required"),
});

export const EditTeamMemberSchema = yup.object().shape({
  role: yup.string().required("Role is required"),
});

export const AddProjectSchema = yup.object().shape({
  clientName: yup.string().required("Client is required"),
  projectKey: plainTextYup({
    required: true,
    requiredMessage: "Project Key is required",
    label: "Project key",
  }),
  projectName: plainTextYup({
    required: true,
    requiredMessage: "Project Name is required",
    label: "Project name",
  }),
  startDate: yup
    .string()
    .trim()
    .required("Start Date is required")
    .test("ymd", "Enter a valid start date", (v) => {
      if (v == null || String(v).trim() === "") return true;
      return isValidYmdLocal(v);
    }),
  endDate: yup
    .string()
    .nullable()
    .test("ymd-when-set", "Enter a valid end date", (value) => {
      if (value == null || String(value).trim() === "") return true;
      return isValidYmdLocal(String(value).trim());
    })
    .test("end-after-start", "End date must be on or after start date", function (value) {
      const v = value != null ? String(value).trim() : "";
      if (v === "") return true;
      const start = this.parent.startDate;
      const startStr = start != null ? String(start).trim() : "";
      if (!isValidYmdLocal(v) || !isValidYmdLocal(startStr)) return true;
      return v >= startStr;
    }),
  hoursLimitPeriod: yup.string().required("Hours Limit Period is required"),
  hoursLimit: yup
    .mixed()
    .transform((value, originalValue) => (originalValue === "" ? undefined : Number(value)))
    .test(
      "is-number",
      "Hours Limit must be a number",
      (value) => value === undefined || !isNaN(value)
    )
    .nullable(),
  status: yup.string().required("Status is required"),
});

export const LogTimeSchema = yup.object().shape({
  date: yup.date().required("This field is required").typeError("Please select a valid date"),
  duration: yup
    .string()
    .required("Duration is required")
    .test(
      "not-zero",
      "Duration is required",
      (value) => value && value.trim() !== "" && value !== "0h"
    )
    .test("valid-jira-duration", function (value) {
      if (!value || value.trim() === "" || value.trim() === "0h") return true;
      const r = parseJiraLogDurationToMinutes(value);
      if (r.ok && r.minutes > 0) return true;
      return this.createError({
        message:
          r.error ||
          "Invalid duration. Use decimal hours (2.5h, .25h), minutes (15m), or combine (1h 30m).",
      });
    }),
  description: yup.string().nullable(),
  issue: yup.object().nullable(),
  logAnother: yup.boolean(),
});

export const EditProfileSchema = yup.object().shape({
  name: yup.string().trim().required("Full Name is required"),
  email: yup.string().email("Please enter a valid email address").required("Email is required"),
  phoneNumber: yup.string().trim().nullable(),
  timezone: yup.string().trim().nullable(),
  emailNotifications: yup.boolean(),
});

const passwordRules = yup
  .string()
  .required("Password is required")
  .min(8, "Password must be at least 8 characters")
  .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
  .matches(/[a-z]/, "Password must contain at least one lowercase letter")
  .matches(/[0-9]/, "Password must contain at least one number")
  .matches(
    /[!@#$%^&*(),.?":{}|<>_\-+=[\]\\';/`~]/,
    "Password must contain at least one special character"
  );

export const ChangePasswordSchema = yup.object().shape({
  currentPassword: yup.string().required("Current password is required"),
  newPassword: passwordRules.test(
    "different-from-current",
    "New password must be different from current password",
    function (value) {
      return value !== this.parent.currentPassword;
    }
  ),
  confirmPassword: yup
    .string()
    .required("Please confirm your new password")
    .oneOf([yup.ref("newPassword")], "Passwords do not match"),
});

export const ResetPasswordSchema = yup.object().shape({
  newPassword: passwordRules,
  confirmPassword: yup
    .string()
    .required("Please confirm your password")
    .oneOf([yup.ref("newPassword")], "Passwords do not match"),
});

/** Start + due date rules for work items (create task + issue detail). YYYY-MM-DD, real calendar day, due ≥ start. */
const workItemPlanningDateFields = {
  startDate: yup
    .string()
    .trim()
    .required("Start date is required")
    .test("ymd", "Enter a valid start date", (v) => {
      if (v == null || String(v).trim() === "") return true;
      return isValidYmdLocal(v);
    }),
  dueDate: yup
    .string()
    .trim()
    .nullable()
    .test("ymd", "Enter a valid due date", (v) => {
      if (v == null || String(v).trim() === "") return true;
      return isValidYmdLocal(v);
    })
    .test("due-after-start", "Due date must be on or after start date", function (value) {
      const start = this.parent.startDate;
      const startStr = start != null ? String(start).trim() : "";
      const dueStr = value != null ? String(value).trim() : "";
      if (!isValidYmdLocal(dueStr) || !isValidYmdLocal(startStr)) return true;
      return dueStr >= startStr;
    }),
};

export const WorkItemPlanningDatesSchema = yup.object().shape(workItemPlanningDateFields);

/**
 * @param {object} [options]
 * @param {Set<string>|string[]} [options.typeIdsRequiringParent] — Work item type ids (Task, Bug) where `parentId` (epic) is required.
 */
export function buildCreateTaskSchema(options = {}) {
  const raw = options.typeIdsRequiringParent;
  const typeIdsRequiringParent =
    raw instanceof Set
      ? raw
      : Array.isArray(raw)
        ? new Set(raw.map((x) => String(x).trim()).filter(Boolean))
        : new Set();

  return yup.object().shape({
    type: yup.string().trim().required("Work type is required"),
    project: yup.string().trim().required("Project is required"),
    parentId: yup
      .string()
      .trim()
      .when("type", {
        is: (typeVal) => typeIdsRequiringParent.has(String(typeVal ?? "").trim()),
        then: (schema) => schema.required("Parent epic is required"),
        otherwise: (schema) => schema.optional(),
      }),
    summary: plainTextYup({
      required: true,
      requiredMessage: "Summary is required",
      label: "Summary",
      min: 3,
      max: 500,
    }),
    priority: yup.string().trim().required("Priority is required"),
    originalEstimate: yup
      .string()
      .trim()
      .default("")
      .test("estimate-parse", function (value) {
        const r = parseWorkItemEstimateInputToMinutes(value ?? "");
        if (r.ok) return true;
        return this.createError({ message: r.error || "Invalid original estimate." });
      }),
    isBacklog: yup.boolean().default(false),
    ...workItemPlanningDateFields,
  });
}

/** Default schema (no parent-id rule); tests and legacy imports. Prefer {@link buildCreateTaskSchema} in forms. */
export const CreateTaskSchema = buildCreateTaskSchema();
